import { useState, useMemo } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, SlidersHorizontal } from "lucide-react";
import { BundleCard } from "@/components/bundles/BundleCard";
import { BundleFilters } from "@/components/bundles/BundleFilters";
import { ProductCardSkeleton } from "@/components/ProductCardSkeleton";
import { useRetailers, useKitTypes, useBundleVersions, useBundles } from "@/hooks/useBundles";
import { useIsMobile } from "@/hooks/use-mobile";
import type { BundleFilters as BundleFiltersType } from "@/contracts/bundles";
export default function GearBundles() {
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRetailers, setSelectedRetailers] = useState<string[]>([]);
  const [selectedKitTypes, setSelectedKitTypes] = useState<string[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Fetch filter options
  const {
    data: retailers = [],
    isLoading: loadingRetailers
  } = useRetailers();
  const {
    data: kitTypes = [],
    isLoading: loadingKitTypes
  } = useKitTypes();
  const {
    data: versions = [],
    isLoading: loadingVersions
  } = useBundleVersions();

  // Build filters for bundles query
  const filters: BundleFiltersType = useMemo(() => ({
    search: searchQuery || undefined,
    retailer_slug: selectedRetailers.length > 0 ? selectedRetailers[0] : undefined,
    kit_type_slug: selectedKitTypes.length > 0 ? selectedKitTypes[0] : undefined,
    version_slug: selectedVersions.length > 0 ? selectedVersions[0] : undefined,
    is_published: true,
    limit: 50,
    offset: 0
  }), [searchQuery, selectedRetailers, selectedKitTypes, selectedVersions]);
  const {
    data: bundles = [],
    isLoading: loadingBundles
  } = useBundles(filters);

  // Filter handlers
  const handleRetailerToggle = (slug: string) => {
    setSelectedRetailers(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [slug]);
  };
  const handleKitTypeToggle = (slug: string) => {
    setSelectedKitTypes(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
  };
  const handleVersionToggle = (slug: string) => {
    setSelectedVersions(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [slug]);
  };
  const handleClearFilters = () => {
    setSelectedRetailers([]);
    setSelectedKitTypes([]);
    setSelectedVersions([]);
    setSearchQuery("");
  };
  const isLoading = loadingRetailers || loadingKitTypes || loadingVersions || loadingBundles;
  return <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Agent Tools & Gear    </h1>
          <p className="text-muted-foreground">
            Curated equipment packages for real estate professionals. Find everything you need in one place.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="flex gap-3">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Search bundles..." className="pl-10" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            {isMobile && <Button variant="outline" size="icon" onClick={() => setFiltersOpen(true)}>
                <SlidersHorizontal className="h-4 w-4" />
              </Button>}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Filters (Desktop only) */}
          {!isMobile && <div className="lg:col-span-1">
              {!isLoading && <BundleFilters retailers={retailers} kitTypes={kitTypes} versions={versions} selectedRetailers={selectedRetailers} selectedKitTypes={selectedKitTypes} selectedVersions={selectedVersions} onRetailerToggle={handleRetailerToggle} onKitTypeToggle={handleKitTypeToggle} onVersionToggle={handleVersionToggle} onClearFilters={handleClearFilters} />}
            </div>}
          
          {/* Mobile Filters Drawer */}
          {isMobile && !isLoading && <BundleFilters retailers={retailers} kitTypes={kitTypes} versions={versions} selectedRetailers={selectedRetailers} selectedKitTypes={selectedKitTypes} selectedVersions={selectedVersions} onRetailerToggle={handleRetailerToggle} onKitTypeToggle={handleKitTypeToggle} onVersionToggle={handleVersionToggle} onClearFilters={handleClearFilters} open={filtersOpen} onOpenChange={setFiltersOpen} />}

          {/* Right Content - Bundle Grid */}
          <div className={isMobile ? "col-span-1" : "lg:col-span-3"}>
            {/* Results Header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {isLoading ? 'Loading...' : `${bundles.length} bundles found`}
              </p>
            </div>

            {/* Bundle Grid */}
            {isLoading ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <ProductCardSkeleton key={i} />)}
              </div> : bundles.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {bundles.map(bundle => <BundleCard key={bundle.id} bundle={bundle} />)}
              </div> : <div className="text-center py-12">
                <p className="text-muted-foreground">
                  No bundles found. Try adjusting your filters.
                </p>
              </div>}
          </div>
        </div>
      </main>

      <Footer />
    </div>;
}